/**
 * Simple Authentication Service
 * Built-in JWT authentication for the dashboard
 */

import CryptoJS from 'crypto-js';
import usersData from '../data/users.json';

// Load users from JSON file
const users = usersData as any[];

// Hash password using crypto-js (browser compatible)
const hashPassword = (password: string): string => {
  return CryptoJS.SHA256(password + 'volta-salt-2025').toString();
};

// Verify password
const verifyPassword = (password: string, hash: string): boolean => {
  const inputHash = hashPassword(password);
  return inputHash === hash;
};

// Generate JWT token
const generateToken = (user: any): string => {
  const payload = {
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    exp: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
  };
  
  // Simple JWT-like token (base64 encoded)
  return btoa(JSON.stringify(payload));
};

// Verify JWT token
const verifyToken = (token: string): any => {
  try {
    const payload = JSON.parse(atob(token));
    if (payload.exp < Date.now()) {
      return null; // Token expired
    }
    return payload;
  } catch (error) {
    return null;
  }
};

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  lastLogin?: string | null;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data?: {
    token: string;
    user: User;
  };
}

class AuthService {
  private static instance: AuthService;
  private tokenKey = 'auth_token';
  private userKey = 'auth_user';

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Login user
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    try {
      // Find user by username or email
      const user = users.find(u => 
        (u.username === username || u.email === username) && u.isActive
      );

      if (!user) {
        return {
          success: false,
          message: 'Invalid credentials'
        };
      }

      // Verify password
      const isPasswordValid = verifyPassword(password, user.password);
      if (!isPasswordValid) {
        return {
          success: false,
          message: 'Invalid credentials'
        };
      }

      // Update last login
      user.lastLogin = new Date().toISOString();

      // Generate token
      const token = generateToken(user);

      const userResponse: User = {
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role,
        lastLogin: user.lastLogin
      };

      // Store token and user data
      this.setToken(token);
      this.setUser(userResponse);

      return {
        success: true,
        message: 'Login successful',
        data: {
          token,
          user: userResponse
        }
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Authentication failed'
      };
    }
  }

  /**
   * Logout user
   */
  logout(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Get stored user
   */
  getUser(): User | null {
    const userJson = localStorage.getItem(this.userKey);
    if (userJson) {
      try {
        return JSON.parse(userJson);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        return null;
      }
    }
    return null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Verify stored token
   */
  verifyToken(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const payload = verifyToken(token);
    if (!payload) {
      this.logout();
      return false;
    }
    return true;
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<User | null> {
    if (!this.verifyToken()) {
      return null;
    }
    return this.getUser();
  }

  /**
   * Store token
   */
  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  /**
   * Store user
   */
  private setUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Hash password (for adding users)
   */
  hashPassword(password: string): string {
    return hashPassword(password);
  }

  /**
   * Add a new user (for admin functionality)
   */
  async addUser(userData: {
    username: string;
    email: string;
    password: string;
    role: string;
  }): Promise<boolean> {
    try {
      // Check if user already exists
      const existingUser = users.find(u => 
        u.username === userData.username || u.email === userData.email
      );

      if (existingUser) {
        return false;
      }

      // Hash password
      const hashedPassword = hashPassword(userData.password);

      // Create new user
      const newUser = {
        id: users.length > 0 ? Math.max(...users.map(u => u.id)) + 1 : 1,
        username: userData.username,
        email: userData.email,
        password: hashedPassword,
        role: userData.role,
        isActive: true,
        createdAt: new Date().toISOString(),
        lastLogin: null
      };

      users.push(newUser);
      return true;
    } catch (error) {
      console.error('Error adding user:', error);
      return false;
    }
  }
}

// Export singleton instance
const authService = AuthService.getInstance();
export default authService;
